import is from 'is_js';
import invariant from 'invariant';
import { moment, PrivilegeChecker } from 'omni-common-ui';
import { List, Map } from 'immutable';
import Privileges from 'domain/Privileges';
import FinancialClosingMoment from 'domain/FinancialClosingMoment';
import { actions as Group } from 'containers/Group';
import { isActive } from 'containers/Group/status';

export function calculateDefaultSession(sessionPlansArg, nowArg = moment()) {
  const now = moment.parseZone(nowArg);
  const sessionPlans = new List(sessionPlansArg);
  let defaultSession = findDefaultSession(sessionPlans, now);

  if (is.undefined(defaultSession)) {
    defaultSession = sessionPlans.get(0);
  }

  return defaultSession;
}

export function calculateDefaultUnitSession(sessionPlansArg, nowArg = moment()) {
  const now = moment.parseZone(nowArg);
  const sessionPlans = new List(sessionPlansArg);
  let defaultSession = findDefaultUnitSession(sessionPlans, now);

  if (is.undefined(defaultSession)) {
    defaultSession = sessionPlans.find(
      (sessionPlan) => Array.isArray(sessionPlan.unitIds) &&
        sessionPlan.unitIds.length > 0
    );
    return is.undefined(defaultSession) ? sessionPlans.get(0) : defaultSession;
  }

  return defaultSession;
}

export function getDefaultSession(state, groupId) {
  invariant(is.truthy(groupId), 'expecting a groupId argument');
  const now = moment();
  const sessionPlans = state.get('rootReducer').get('sessionPlans').toList().filter(
    (plan) => plan.groupId === groupId)
  .sortBy(
    (plan) => plan.index);
  let defaultSession = findDefaultSession(sessionPlans, now);
  if (is.undefined(defaultSession)) {
    defaultSession = sessionPlans.get(0);
  }

  return defaultSession;
}

export function getDefaultUnits(state, groupId) {
  const defaultSession = getDefaultSession(state, groupId);

  return defaultSession.unitIds;
}

export function getDefaultUnitSession(state, groupId) {
  invariant(is.truthy(groupId), 'expecting a groupId argument');
  const now = moment();
  const sessionPlans = state.get('rootReducer').get('sessionPlans').toList().filter(
    (plan) => plan.groupId === groupId)
  .sortBy(
    (plan) => plan.index
  );
  return findDefaultUnitSession(sessionPlans, now);
}

export function findNextUnitSession(sessionPlans, units, currentSessionId) {
  const unitId = getNextOrPreviousUnitBySessionId(sessionPlans, units, currentSessionId, true);
  if (is.existy(unitId)) {
    return sessionPlans.find((sessionPlan) => sessionPlan.unitId === unitId);
  }
  return getNextOrPreviousSessionWithoutUnitId(sessionPlans, units, currentSessionId, true);
}

export function findPreviousUnitSession(sessionPlans, units, currentSessionId) {
  const unitId = getNextOrPreviousUnitBySessionId(sessionPlans, units, currentSessionId, false);
  if (is.existy(unitId)) {
    return sessionPlans.find((sessionPlan) => sessionPlan.unitId === unitId);
  }
  return getNextOrPreviousSessionWithoutUnitId(sessionPlans, units, currentSessionId, false);
}

// ----- //

function findDefaultSession(sessionPlans, now) {
  return sessionPlans.findLast((plan) => {
    if (now.isSameOrAfter(plan.period.start)) {
      return true;
    }
    return false;
  });
}

export function getPreviousUnitIdBySessionId(state, groupId, currentSessionId) {
  const sessions = getOrderSessions(state, groupId);
  const currentSessionIndex = sessions.findIndex(
    (session) => session.id === currentSessionId
  );
  const previousUnitSession = sessions.findLast(
    (session, index) => Array.isArray(session.unitIds) &&
      session.unitIds.length > 0 &&
      index < currentSessionIndex
  );
  return previousUnitSession && previousUnitSession.unitIds[0];
}

export function getNextUnitIdBySessionId(state, groupId, currentSessionId) {
  const sessions = getOrderSessions(state, groupId);
  const currentSessionIndex = sessions.findIndex(
    (session) => session.id === currentSessionId
  );
  const nextUnitSession = sessions.find(
    (session, index) => Array.isArray(session.unitIds) &&
      session.unitIds.length > 0 &&
      index > currentSessionIndex
  );
  return nextUnitSession && nextUnitSession.unitIds[0];
}

function getNextOrPreviousSessionWithoutUnitId(sessionPlans, units, currentSessionId, next) {
  const sessionList = sessionPlans.toList();
  const currentSessionIndex = sessionList.findIndex(
    (session) => session.id === currentSessionId
  );
  const nextUnitSession = sessionList.find(
    (session, index) => session.unitId && index > currentSessionIndex
  );
  const previousUnitSession = sessionList.findLast(
    (session, index) => session.unitId && index < currentSessionIndex
  );
  let unitId = next ? nextUnitSession && nextUnitSession.unitId :
    previousUnitSession && previousUnitSession.unitId;
  if (nextUnitSession &&
    previousUnitSession &&
    nextUnitSession.unitId &&
    nextUnitSession.unitId === previousUnitSession.unitId) {
    unitId = getNextOrPreviousUnit(units, nextUnitSession.unitId, next);
  }
  return sessionPlans.find((sessionPlan) => sessionPlan.unitId === unitId);
}

function findDefaultUnitSession(sessionPlans, now) {
  return sessionPlans.findLast((plan) => {
    if (now.isSameOrAfter(plan.period.start) &&
      Array.isArray(plan.unitIds) &&
      plan.unitIds.length > 0) {
      return true;
    }
    return false;
  });
}

function getNextOrPreviousUnitBySessionId(sessionPlans, unitInfo, currentSessionId, next) {
  if (sessionPlans && unitInfo) {
    const currentUnitIds = sessionPlans.find(
      (session) => session.id === currentSessionId
    ).unitIds;
    const currentUnitId = Array.isArray(currentUnitIds) ?
      currentUnitIds[0] : currentUnitIds;
    if (is.existy(currentUnitId)) {
      return getNextOrPreviousUnit(unitInfo, currentUnitId, next);
    }
  }
}

function getNextOrPreviousUnit(unitInfo, unitId, next) {
  const units = unitInfo && unitInfo.toList();
  const currentUnitIndex = units.findIndex((unit) =>
    unit && unit.id === `${unitId}`
  );
  const unitIndex = next ? currentUnitIndex + 1 : currentUnitIndex - 1;
  if (unitIndex < 0 || unitIndex >= units.size) {
    return unitIndex < 0 ? units.get(0) : units.get(units.size - 1);
  }
  return units.get(unitIndex).id;
}

export function getSessions(state, groupId) {
  return state.get('rootReducer').get('sessionPlans') &&
    state.get('rootReducer').get('sessionPlans').filter((session) => session.groupId === groupId);
}

export function getOrderSessions(state, groupId) {
  return getSessions(state, groupId)
      .filter((session) => session.groupId === groupId)
      .toList()
      .sortBy((session) => session.index);
}

export function getSession(state, sessionId) {
  return state.get('rootReducer').get('sessionPlans').get(sessionId);
}

export function getFirstUnitOfSession(state, sessionId) {
  const session = getSession(state, sessionId);
  if (session && Array.isArray(session.unitIds) && session.unitIds.length > 0) {
    return session.unitIds[0];
  }
  return 0;
}

export function getFirstSessionOfUnit(state, groupId, unitId) {
  return getOrderSessions(state, groupId)
    .filter((session) =>
      Array.isArray(session.unitIds) && session.unitIds.includes(unitId))
    .get(0);
}

export function isFutureSession(state, sessionId) {
  const session = getSession(state, sessionId);
  invariant(session, 'Invalid session id.');
  return moment().isBefore(session.period.start);
}

export function isMarkable(state, groupId, currentSessionId) {
  let markable = null;
  const group = Group.getGroup(state, groupId);
  const sessions = getSessions(state, groupId);
  if (Map.isMap(sessions) && sessions.size !== 0) {
    const currentSession = sessions.find((plan) =>
    plan.id === currentSessionId);
    const { attendanceId } = currentSession;
    if (moment().isBefore(currentSession.period.start)) {
      markable = false;  // future session
    } else if (is.existy(attendanceId)) {
      const financialClosingMoment = FinancialClosingMoment.nextFrom(
        currentSession.period.start);
      const financialCheck = moment().isBefore(financialClosingMoment) ||
        PrivilegeChecker.hasPrivilege(state, Privileges.MarkAttendance.IgnoreFinanceDate);
      markable = financialCheck && isActive(group.status);
    }
  }
  return markable;
}

export function calculatePreviousSession(sessionPlansArg, nowArg = moment()) {
  const now = moment.parseZone(nowArg);
  const sessionPlans = new List(sessionPlansArg);
  const defaultSession = findDefaultSession(sessionPlans, now);

  let previousSession;
  if (is.undefined(defaultSession)) {
    previousSession = sessionPlans.get(0);
  } else {
    previousSession = findPreviousSession(sessionPlans, defaultSession);
  }
  previousSession = previousSession || sessionPlans.get(0);

  return previousSession;
}

export function getPreviousSession(state, groupId) {
  invariant(is.truthy(groupId), 'expecting a groupId argument');
  const now = moment();
  const sessionPlans = state.get('rootReducer').get('sessionPlans').toList().filter(
    (plan) => plan.groupId === groupId)
  .sortBy(
    (plan) => plan.index);
  const defaultSession = findDefaultSession(sessionPlans, now);

  let previousSession;
  if (is.undefined(defaultSession)) {
    previousSession = sessionPlans.get(0);
  } else {
    previousSession = findPreviousSession(sessionPlans, defaultSession);
  }
  previousSession = previousSession || sessionPlans.get(0);

  return previousSession;
}

export function getNextSession(state, groupId, currentSessionId) {
  const sessionPlans = getOrderSessions(state, groupId);
  const currentSession = getSession(state, currentSessionId);

  let nextSession;
  if (is.undefined(currentSession)) {
    nextSession = sessionPlans.last();
  } else {
    nextSession = findNextSession(sessionPlans, currentSession);
  }
  nextSession = nextSession || sessionPlans.last();

  return nextSession;
}

function findPreviousSession(sessionPlans, defaultSession) {
  return sessionPlans.findLast((plan) => {
    if (moment.parseZone(plan.period.end).isBefore(defaultSession.period.start)) {
      return true;
    }
    return false;
  });
}

function findNextSession(sessionPlans, defaultSession) {
  return sessionPlans.filter((plan) => {
    if (moment.parseZone(plan.period.start).isAfter(defaultSession.period.end)) {
      return true;
    }
    return false;
  }).first();
}



// WEBPACK FOOTER //
// ./data/SessionPlans/accessors.js